import moment from 'moment';
import db from '@/store/db';
import { recalculatePartProperties } from '@/common/partHelpers';
import log from '@/logger';
import todoTemplateApi from '../todoItem/todoTemplateApi';

// prettier-ignore
const managerOccupations = [
  'Client 1', 'Client 2', 'Production Commander', 'Project Director', 'Control Manager', 'Architect 1', 'Architect 2',
  'Constructor', 'Frame Design', 'Electrician', 'Ventilation', 'VS', 'Sprinklers', 'Risk Analysis',
  'Outer Water/Drainage', 'Landscape Architect', 'Fire', 'Geotechnics', 'Energy Calculation', 'Acoustic',
  'Service Heating', 'Service VA', 'Service Electricity', 'Service Telecom', 'Kommun', 'Main Inspector',
  'Inspection Construction', 'Inspection VVS', 'Inspection Electricity', 'Inspection Ground', 'Ventilation Inspection',
];

// prettier-ignore
const subcontractorOccupations = [
  'Site Manager', 'Site Assistant', 'Site Assistant 2', 'Site Assistant 3', 'Supply Controller', 'Surveyor',
  'Decontamination', 'Ground', 'Concrete', 'Waste', 'Blasting', 'Sheet Piling', 'Piling', 'Foundation', 'Frame',
  'Scaffolding', 'Carpenter', 'VS', 'Ventilation', 'Waterproofing', 'Core drilling', 'Estrich', 'Sheet Metal',
  'Roofing Membrane', 'Roofing Sheet Metal', 'Insulation', 'Loose wool', 'Facade Puts', 'Facade Sheet Metal',
  'Facade Brick', 'Facade Wood', 'Roofing Tiles', 'Painter', 'Parquet', 'Tiling', 'Electrician', 'Solar Panels',
  'Monitoring/Contol System', 'Sprinklers', 'Borehole Heating', 'Elevator', 'Balcony railing', 'Locksmith',
  'Aluminum Doors', 'Lining/Base', 'Appliances', 'Seal/Jointing',
];

const marketOccupations = ['Guarantee Contractor', 'Property Service'];

export default {
  prepareProjectEntity(values) {
    const project = {
      type: 'Project',
      projectNumber: '',
      title: 'Empty Project',
      children: [],
      archived: false,
      projectType: '',
      buildingType: '',
      developingCompanyId: '',
      developingManagerId: '',
      lastUpdate: {},
      projectBta: '',
      links: {
        workEnvironmentPlan: '',
        selfMonitoring: '',
        projectDescription: '',
        qualityDocument: '',
        environmentalPlan: '',
      },
      lockedForEditingBy: null,
      lockedForEditingAt: null,
      lockedDragNDrop: false,
      projectIcon: '',
      description: '',
      address: {
        street: '',
        zipCode: '',
        city: '',
      },
      diaryStartDate: '',
      ...(typeof values === 'object' && values != null ? values : {}),
    };

    if (!project.projectManagers) {
      project.projectManagers = managerOccupations.map((occupation) => ({ occupation, companyId: '', personId: '' }));
    }

    if (!project.projectSubcontractors) {
      project.projectSubcontractors = subcontractorOccupations.map((occupation) => ({
        occupation,
        companyId: '',
        personId: '',
      }));
    }

    if (!project.aftermarkets) {
      project.aftermarkets = marketOccupations.map((occupation) => ({ occupation, companyId: '', personId: '' }));
    }

    if (!project.createdAt) {
      project.createdAt = moment().format();
    }

    if (!project.projectStartdate) {
      project.projectStartdate = moment().format('YYYY-MM-DD');
    }

    if (!project.projectEnddate) {
      project.projectEnddate = moment().add(1, 'year').format('YYYY-MM-DD');
    }

    return project;
  },

  logProjectAction: function (log) {
    if (db.currentProjectLogDb.post) {
      return db.currentProjectLogDb.post(log);
    }
    return new Promise(() => {
      log.error('api:logProjectAction: Unable to log', log);
      return false;
    });
  },

  async getProjectLog({ projectId }) {
    const logDb = await db.getLogDb(projectId);

    try {
      const params = {
        include_docs: true,
        startkey: [projectId, '9999-12-31T23:59:59.999Z'],
        endkey: [projectId, '0000-12-31T23:59:59.999Z'],
        limit: 1500,
        descending: true,
      };
      const [projectLogs, organizationLogs] = await Promise.all([
        logDb.query('projectLog', { ...params }),
        db.organisationLogDb.query('projectLog', { ...params }),
      ]);

      const logs = projectLogs.rows.map(({ doc }) => doc);
      organizationLogs.rows.forEach(({ doc }) => logs.push(doc));

      return logs;
    } catch (error) {
      log.error('api:getProjectLog', error);
    }
  },

  updateProjectList: function () {
    return db.organisationDb
      .query('projectList')
      .then((result) => {
        var populated = [];
        for (var row of result.rows) {
          var project = row.value;
          project._id = row.id;
          populated.push(project);
        }

        return populated;
      })
      .catch(function (err) {
        log.error('Unable to load project list', err);
        return [];
      });
  },

  async createProject(newProjectProperties) {
    const newProject = this.prepareProjectEntity(newProjectProperties);

    const doc = await db.organisationDb.post(newProject);
    newProject._id = doc.id;
    newProject._rev = doc.rev;

    return newProject;
  },

  importProject({ dispatch, state, projectData }) {
    const newProject = this.prepareProjectEntity(projectData.project);

    newProject.title += ' ' + moment().format('YYYY-MM-DD');
    delete newProject._id;
    delete newProject._rev;
    newProject.lastUpdate = {};

    var newParts = [];
    for (var property in projectData.parts) {
      if (projectData.parts.hasOwnProperty(property)) {
        var newPart = Object.assign(this.getDefaultPart(), projectData.parts[property]);
        newPart.originalId = newPart._id;
        delete newPart._id;
        delete newPart.projectId;
        delete newPart._rev;
        newPart.lastUpdate = {};
        newParts.push(newPart);
      }
    }

    var stageFn = (stage) => {
      var newStage = Object.assign(this.getDefaultStage(), stage);
      newStage.originalId = newStage._id;
      delete newStage._id;
      delete newStage._rev;
      delete newStage.projectId;
      newStage.lastUpdate = {};
      newStages.push(newStage);
    };

    var newStages = [];
    // This used to be an array, so for backwards compatibility, we handle both
    if (Array.isArray(projectData.stages)) {
      for (var stage of projectData.stages) {
        stageFn(stage);
      }
    } else {
      for (var stageId in projectData.stages) {
        if (projectData.stages.hasOwnProperty(stageId)) {
          stageFn(projectData.stages[stageId]);
        }
      }
    }
    // var api = this;
    return Promise.all([db.organisationDb.post(newProject), newProject])
      .then(async ([doc, newProject]) => {
        newProject._id = doc.id;
        newProject._rev = doc.rev;

        await this.switchDatabase(newProject._id, state, dispatch);

        var promises = [];
        for (stage of newStages) {
          stage.projectId = newProject._id;
          var newPromise = Promise.all([this.saveStage(stage), stage]).then(([savedStage, oldStage]) => {
            oldStage._id = savedStage._id;
            oldStage._rev = savedStage._rev;
            return oldStage;
          });
          promises.push(newPromise);
        }

        for (var part of newParts) {
          part.projectId = newProject._id;
          part.inspectionId = '';
          newPromise = Promise.all([this.savePart(part), Promise.resolve(part)]).then(([savedPart, oldPart]) => {
            oldPart._id = savedPart.id;
            oldPart._rev = savedPart.rev;
            return oldPart;
          });
          promises.push(newPromise);
        }

        return Promise.all(promises);
      })
      .then((all) => {
        this.remapChildrenIds(newProject.children, newParts);
        this.remapChildrenIds(newProject.children, newStages);
        return this.saveProject(newProject);
      });
  },
  copyChildren: async function ({ projectData, newProjectId }) {
    var newParts = [];
    var firstStartDate = moment('9999-12-31');
    var lastEndDate = moment('0000-01-01');
    for (var property in projectData.parts) {
      if (projectData.parts.hasOwnProperty(property)) {
        var newPart = Object.assign(this.getDefaultPart(), projectData.parts[property], { colour: '#ff0000' });
        newPart.originalId = newPart._id;
        delete newPart._id;
        delete newPart.projectId;
        delete newPart._rev;
        newPart.lastUpdate = {};
        newParts.push(newPart);

        if (firstStartDate.isAfter(newPart.startdate)) {
          firstStartDate = moment(newPart.startdate);
        }
        if (lastEndDate.isBefore(newPart.expectedDonedate)) {
          lastEndDate = moment(newPart.expectedDonedate);
        }
      }
    }

    var stageFn = (stage) => {
      var newStage = Object.assign(this.getDefaultStage(), stage, { colour: '#ff0000' });
      newStage.originalId = newStage._id;
      delete newStage._id;
      delete newStage._rev;
      delete newStage.projectId;
      newStage.lastUpdate = {};
      newStages.push(newStage);
    };

    var newStages = [];
    // This used to be an array, so for backwards compatibility, we handle both
    if (Array.isArray(projectData.stages)) {
      for (var stage of projectData.stages) {
        stageFn(stage);
      }
    } else {
      for (var stageId in projectData.stages) {
        if (projectData.stages.hasOwnProperty(stageId)) {
          stageFn(projectData.stages[stageId]);
        }
      }
    }

    var newProject = await this.fetchProject(newProjectId);
    var diffDays = -firstStartDate.diff(newProject.projectStartdate, 'days');

    newProject.children = projectData.project.children.slice();

    var promises = [];
    for (stage of newStages) {
      stage.projectId = newProject._id;
      let newPromise = Promise.all([this.saveStage(stage), stage]).then(([savedStage, oldStage]) => {
        oldStage._id = savedStage._id;
        oldStage._rev = savedStage._rev;
        return oldStage;
      });
      promises.push(newPromise);
    }

    for (var part of newParts) {
      part.projectId = newProject._id;

      var update = {
        updatePart: part,
        newPartProperties: {
          startdate: moment(part.startdate).add(diffDays, 'days').format('YYYY-MM-DD'),
          inspectionId: '',
        },
      };
      recalculatePartProperties(update);
      part = Object.assign(part, update.newPartProperties);

      let newPromise = Promise.all([this.savePart(part), Promise.resolve(part)]).then(([savedPart, oldPart]) => {
        oldPart._id = savedPart.id;
        oldPart._rev = savedPart.rev;
        return oldPart;
      });
      promises.push(newPromise);
    }

    for (let i = 0; i < projectData.todoTemplates.length; i++) {
      let todoTemplate = projectData.todoTemplates[i];
      var newTodoTemplate = Object.assign(todoTemplateApi.getDefaultTodoTemplate(), todoTemplate, {
        projectId: newProject._id,
      });
      newTodoTemplate.originalId = newTodoTemplate._id;
      delete newTodoTemplate._id;
      delete newTodoTemplate._rev;
      newTodoTemplate.lastUpdate = {};
      let newPromise = todoTemplateApi.createTodoTemplate(newProject._id, newTodoTemplate);
      promises.push(newPromise);
    }

    return Promise.all(promises).then((all) => {
      newProject.projectEnddate = moment(lastEndDate).add(diffDays, 'days').format('YYYY-MM-DD');
      this.remapChildrenIds(newProject.children, newParts);
      this.remapChildrenIds(newProject.children, newStages);

      return this.saveProject(newProject);
    });
  },
  saveProject: function (project, reason) {
    if (project._id) {
      return db.organisationDb
        .put(project, reason)
        .then((response) => {
          if (response.id && !response._id) {
            response._id = response.id;
            delete response.id;
          }
          if (response.rev) {
            response._rev = response.rev;
            delete response.rev;
          }

          return response;
        })
        .catch((e) => {
          log.error('projectApi::saveProject:put:error', e);
          throw new Error('Error saving project');
        });
    } else {
      return db.organisationDb.post(project, reason).then((response) => {
        if (response.id && !response._id) {
          response._id = response.id;
          delete response.id;
        }
        if (response.rev) {
          response._rev = response.rev;
          delete response.rev;
        }
        return response;
      });
    }
  },

  async fetchProject(projectId) {
    const project = await db.organisationDb.get(projectId);

    return this.prepareProjectEntity(project);
  },

  getProjectItem: function (id) {
    return db.currentProjectDb
      .get(id)
      .then((object) => {
        return object;
      })
      .catch((e) => {
        return db.organisationDb.get(id).then((object) => {
          return object;
        });
      });
  },
};



// WEBPACK FOOTER //
// ./src/store/project/projectApi.js